import { TCart } from 'widgets/cart/CartMgr';
import { scrollWindowTo } from 'widgets/toolbox/scroll';
import { submitFormJson } from 'widgets/toolbox/ajax';
import { IAddToCartButton } from './AddToCartMixin';
import { TProcessButtonInstance } from 'widgets/global/ProcessButton';
import { TAddToCartMixin, TAddToCartMixinInstance } from 'widgets/product/AddToCartMixin';

type TAddAllToCartResponse = TServerResponse & {
    cart: TCart;
    messages: Array<unknown>;
}
type TProductSetPID = {
    pid: string;
    qty: number;
};
type TProductSetItem = {
    pid: string;
    quantity: number;
    isAvailable: boolean;
    isAllAttributesSelected: boolean;
    hasAvailableVariant: boolean;
    widget: TAddToCartMixinInstance;
};

/**
 * @description Base AddAllToCartMixin implementation
 *
 * This class is not intended to have a separate DOM representation.
 *
 * @param ProductSet Base widget for extending
 * @returns Add to Cart mixin widget
 */
function AddAllToCartMixinClassCreator(ProductSet: import('widgets/product/ProductSet').TProductSet) {
    /**
     * @category widgets
     * @subcategory product
     * @class AddAllToCartMixin
     * @augments ProductSet
     * @classdesc Represents product set component with the next features:
     * 1. Adds all product set products to the basket
     * 2. Shows alert message after adding product set products to the basket
     *
     * @property {string} data-text-network-error - network error text
     */
    class AddAllToCartMixin extends ProductSet {
        prefs() {
            return {
                pid: '',
                processingRequest: false,
                selectedQuantity: '',
                showMinicartOnProductAdd: false,
                showAlertOnProductAdd: '',
                showMessageOnProductAdd: true,
                addToCartGlobalMsg: '',
                textNetworkError: '',
                accessibilityAlerts: {
                    addedsettocart: ''
                },
                textSelectAvailableOption: '',
                ...super.prefs()
            };
        }

        init() {
            super.init();

            this.eventBus().on('product.updated', 'onProductItemUpdated');
        }

        /**
         * @description Sets an error for passed product from a product set
         * @param product product detail widget
         * @param msg - message string
         */
        showProductError(product: TAddToCartMixinInstance, msg: string) {
            product.getById('addToCartMsg', (addToCartMsg) => {
                const error = true;

                addToCartMsg.render(
                    'template',
                    { msg, error },
                    addToCartMsg.ref('container')
                ).then(() => addToCartMsg.show());
            });
        }

        /**
         * @description Shows error message for all invalid products and sets focus on the first product with error
         * @param productSetItems - product list for validation and setting an error
         * @param invalidProductFilter - predicate, which points on invalid product
         * @param errorMessageGetter - function that returns error message for setting it for invalid product
         * @returns true if error was set, false otherwise
         */
        handleProductSetError(
            productSetItems: Array<TProductSetItem>,
            invalidProductFilter: (item: TProductSetItem) => boolean,
            errorMessageGetter: (item: TProductSetItem) => string
        ) {
            const invalidProducts = productSetItems.filter(invalidProductFilter);

            if (!invalidProducts.length) {
                return false;
            }

            invalidProducts.forEach((productSetItem) => this.showProductError(productSetItem.widget, errorMessageGetter(productSetItem)));

            const firstInvalidProductWidget = invalidProducts[0].widget;

            firstInvalidProductWidget.getById(
                'productName',
                (productName) => {
                    const firstUnselectedProductTitle = productName.ref('titleLink').get();

                    if (!firstUnselectedProductTitle) {
                        return;
                    }

                    firstUnselectedProductTitle.focus();

                    scrollWindowTo(firstUnselectedProductTitle, true);
                }
            );

            return true;
        }

        /**
         * @description Handler for unselected but available product variations
         * @param productSetItems list for products for adding to the cart
         * @returns true if error was set, false otherwise
         */
        handleUnselected(productSetItems: Array<TProductSetItem>): boolean {
            return this.handleProductSetError(
                productSetItems,
                (setItem) => !setItem.isAllAttributesSelected && setItem.hasAvailableVariant,
                (setItem) => setItem.widget.prefs().textSelectOptions
            );
        }

        /**
         * @description Disables process button if all products are unavailable, enables otherwise
         * @param productSetItems list for products for adding to the cart
         */
        updateAddAllToCardButton(productSetItems: Array<TProductSetItem>) {
            this.getById<TProcessButtonInstance>('addToCardGlobal', (processButton) => {
                if (productSetItems.some(product => product.isAvailable)) {
                    processButton.enable();
                } else {
                    processButton.disable();
                }
            });
        }

        /**
         * @description Validates if product can be added to the cart and sets error if not
         * @param productSetItems list for products for adding to the cart
         * @returns true if there are no errors
         */
        isProductsValidForAddingToCart(productSetItems: Array<TProductSetItem>) {
            let isErrorSet = false;

            isErrorSet = this.handleUnselected(productSetItems);

            return !isErrorSet;
        }

        /**
         * @description Returns product set data for the set items
         * @returns result
         */
        getProductSetItems(): Array<TProductSetItem> {
            const AddToCartMixin = <TAddToCartMixin> this.getConstructor('addToCartMixin');

            const productSetItems: Array<TProductSetItem> = [];

            this.eachChild((child) => {
                if (child instanceof AddToCartMixin && child.prefs().productSetItem) {
                    let isOutOfStock = false;

                    child.getById<IAddToCartButton>('addToCart', (addToCart) => {
                        isOutOfStock = Boolean(addToCart.prefs().isOutOfStock);
                    });

                    const prefs = child.prefs();

                    productSetItems.push({
                        pid: prefs.currentProductId || prefs.pid,
                        quantity: prefs.selectedQuantity,
                        isAvailable: prefs.readyToOrder && !isOutOfStock, // is selected and is available
                        isAllAttributesSelected: prefs.isAllAttributesSelected, // is selected but could be unavailable
                        hasAvailableVariant: prefs.hasAvailableVariant, // at least one available variant could be selected
                        widget: child
                    });
                }
            });

            return productSetItems;
        }

        /**
         * @description Returns an array with products available for ordering
         * @param productSetItems - product set data for the set items
         * @returns result
         */
        getPidsObj(productSetItems: Array<TProductSetItem>): Array<TProductSetPID> {
            return productSetItems.filter(setItem => setItem.isAvailable).map((childProduct) => {
                return {
                    pid: childProduct.pid,
                    qty: childProduct.quantity
                };
            });
        }

        /**
         * @description On add all to cart button click handler
         * @param button - button
         * @returns Promise object represents server response for session continuation
         */
        onAddAllToCart(button) {
            if (this.prefs().processingRequest) {
                return Promise.resolve(null);
            }

            const productSetItems = this.getProductSetItems();

            if (!this.isProductsValidForAddingToCart(productSetItems)) {
                return Promise.resolve(null);
            }

            const pidsObj = this.getPidsObj(productSetItems);
            const isFullProductSet = pidsObj.length === productSetItems.length;

            this.setPref('processingRequest', true);
            button.startProcess();

            this.getById(this.prefs().addToCartGlobalMsg, (addToCartGlobalMsg) => addToCartGlobalMsg.hide());

            return submitFormJson(button.prefs().addToCartUrl, {
                pid: this.prefs().pid || button.prefs().pid,
                quantity: this.prefs().selectedQuantity || button.prefs().selectedQuantity,
                pidsObj: JSON.stringify(pidsObj)
            })
                .then((response: TAddAllToCartResponse) => {
                    this.postAddAllToCart(response, isFullProductSet, pidsObj.length);

                    return response;
                })
                .finally(() => {
                    this.setPref('processingRequest', false);
                    button.stopProcess();
                }).catch(() => {
                    this.showAddAllToCartAlert(true, this.prefs().textNetworkError);
                });
        }

        /**
         * @description Handles a result of product set adding to cart
         * @emits "minicart.need.update"
         * @emits "product.added.to.cart"
         * @param response - response
         * @param isFullProductSet - Identifies if all product set items are added
         * @param productsCount - products count to be added to the cart
         */
        postAddAllToCart(response: TAddAllToCartResponse, isFullProductSet: boolean, productsCount: number) {
            if (response.success && response.cart && response.messages) {
                const cartModel = response.cart;

                cartModel.showMinicart = this.prefs().showMinicartOnProductAdd;

                if (response.addToCartOverlayUrl) {
                    this.eventBus().emit('dialogshow', {
                        url: response.addToCartOverlayUrl,
                        headerText: response.addToCartOverlayHeader,
                        bodyClass: 'm-has_dialog',
                        wrapperClasses: 'm-cart_overlay'
                    });
                }

                const isAllProductsAdded = response.messages && (response.messages.length === productsCount);
                const message = isFullProductSet && isAllProductsAdded
                    ? this.prefs().accessibilityAlerts.addedsettocart
                    : response.messages.join('<br>');

                if (this.prefs().showMessageOnProductAdd) {
                    this.showAddAllToCartAlert(false, message);
                }

                this.eventBus().emit('minicart.need.update', cartModel, this);
                this.eventBus().emit('product.added.to.cart', cartModel, this);
            } else {
                this.showAddAllToCartAlert(true, response.errorMessage);
            }
        }

        /**
         * @description Shows alert message after adding a product set to the cart
         * @param error - identifies if message should be shown as an error
         * @param message - message
         */
        showAddAllToCartAlert(error: boolean, message: string) {
            if (!this.prefs().showAlertOnProductAdd) {
                return;
            }

            this.getById(this.prefs().addToCartGlobalMsg, (addToCartGlobalMsg) => {
                addToCartGlobalMsg.render(
                    'template',
                    {
                        msg: message,
                        error
                    },
                    addToCartGlobalMsg.ref('container')
                ).then(() => addToCartGlobalMsg.show());
            });
        }

        onProductItemUpdated() {
            this.updateAddAllToCardButton(this.getProductSetItems());

            if (!this.prefs().showAlertOnProductAdd) {
                return;
            }

            this.getById(this.prefs().addToCartGlobalMsg, (addToCartGlobalMsg) => {
                addToCartGlobalMsg.hide();
            });
        }
    }

    return AddAllToCartMixin;
}

export type TAddAllToCartMixin = ReturnType<typeof AddAllToCartMixinClassCreator>;

export type TAddAllToCartMixinInstance = InstanceType<TAddAllToCartMixin>;

export default AddAllToCartMixinClassCreator;
